import { Controller } from "@hotwired/stimulus"
import { FieldCamera } from "../lib/field_camera"
import { pushForegroundArea, popForegroundArea } from "../field_foreground_time"

// Docked camera strip for the ranging-status scanner (card #4120).
//
// Unlike field-barcode-scanner (a modal sheet that closes on every scan, still
// used by the Shelf tab and the products page), this stays open until the
// Tasker dismisses it, so they can scan a run of products. The page behind it
// stays scrollable and tappable. It only reads barcodes: matching a barcode to
// a row and answering it is lib/ranging_confirm_renderer.js, which listens for
// the same field-barcode-scanner:scanned event the modal scanner dispatches.
//
// The camera drains the phone, so it is stopped whenever it is not needed:
// dismissed, page hidden, page left, or no scan for IDLE_PAUSE_MS (the Tasker
// taps to resume).
const REPEAT_IGNORE_MS = 2000
const IDLE_PAUSE_MS = 60000
const FLASH_MS = 400

export default class extends Controller {
  static targets = ["viewfinder", "status", "flash", "pausedPanel", "pinned", "noMatch"]

  connect() {
    this._camera = null
    this._session = 0
    this._lastBarcode = null
    this._lastScanAt = 0
    this._areaPushed = false
    this._onVisibility = () => { if (document.hidden) this.pause("Camera paused") }
    this._onPageHide = () => this.stopCamera()
    this._onExternalClose = () => this.close()
  }

  disconnect() {
    this._removeGuards()
    this.stopCamera()
    // Card #4838 -- a Turbo navigation away while the strip is still open
    // (no close() in between) must not leave "scanner" stuck on the
    // foreground-time area override stack forever.
    if (this._areaPushed) {
      popForegroundArea()
      this._areaPushed = false
    }
  }

  // ranging-barcode:open@document
  open() {
    this.element.classList.remove("hidden")
    this.pinnedTarget.replaceChildren()
    this.noMatchTarget.classList.add("hidden")
    this._addGuards()
    // Card #4838 -- "in-app scanner" foreground-time area for as long as the
    // docked strip is open, even while the camera itself is paused to save
    // battery (pause()/resume() don't touch this -- the Tasker is still on
    // the scanner screen either way).
    pushForegroundArea("scanner")
    this._areaPushed = true
    document.dispatchEvent(new CustomEvent("ranging-barcode:opened"))
    this.startCamera()
  }

  async close() {
    this._removeGuards()
    await this.stopCamera()
    this.element.classList.add("hidden")
    this.pausedPanelTarget.classList.add("hidden")
    this.pinnedTarget.replaceChildren()
    this.noMatchTarget.classList.add("hidden")
    if (this._areaPushed) {
      popForegroundArea()
      this._areaPushed = false
    }
    document.dispatchEvent(new CustomEvent("ranging-barcode:closed"))
  }

  resume() {
    this.startCamera()
  }

  async flipCamera() {
    if (!this._camera) return
    const session = this._session
    await this._camera.flip()
    this._armIdleTimer()
    this._camera.start(barcode => this._onScan(barcode))
      .catch(() => { if (session === this._session) this._setStatus("Camera unavailable") })
  }

  async startCamera() {
    await this.stopCamera()
    const session = ++this._session
    this.pausedPanelTarget.classList.add("hidden")
    this._setStatus("Point camera at a barcode")
    this._camera = new FieldCamera(this.viewfinderTarget.id)
    this._armIdleTimer()
    try {
      await this._camera.start(barcode => this._onScan(barcode))
    } catch (_error) {
      if (session !== this._session) return
      this._clearIdleTimer()
      this._camera = null
      this._setStatus("Camera unavailable. Allow camera access for WIN Field.")
    }
  }

  async stopCamera() {
    this._session++
    this._clearIdleTimer()
    const camera = this._camera
    this._camera = null
    await camera?.stop()
  }

  // Stops the camera but leaves the strip open, with a tap-to-resume panel.
  async pause(message = "Camera paused") {
    if (!this._camera) return
    await this.stopCamera()
    this._setStatus(message)
    this.pausedPanelTarget.classList.remove("hidden")
  }

  _onScan(barcode) {
    const now = Date.now()
    if (barcode === this._lastBarcode && now - this._lastScanAt < REPEAT_IGNORE_MS) return
    this._lastBarcode = barcode
    this._lastScanAt = now

    this._armIdleTimer()
    this._flash()
    this._setStatus(`Scanned ${barcode}`)
    this.dispatch("scanned", { prefix: "field-barcode-scanner", detail: { barcode } })
  }

  // iOS has no vibrate, so each accepted scan flashes the frame green instead.
  _flash() {
    this.flashTarget.classList.remove("opacity-0")
    clearTimeout(this._flashTimer)
    this._flashTimer = setTimeout(() => this.flashTarget.classList.add("opacity-0"), FLASH_MS)
  }

  _armIdleTimer() {
    this._clearIdleTimer()
    this._idleTimer = setTimeout(() => this.pause("Paused to save battery"), IDLE_PAUSE_MS)
  }

  _clearIdleTimer() {
    clearTimeout(this._idleTimer)
    this._idleTimer = null
  }

  _addGuards() {
    document.addEventListener("visibilitychange", this._onVisibility)
    window.addEventListener("pagehide", this._onPageHide)
    document.addEventListener("ranging-barcode:close", this._onExternalClose)
  }

  _removeGuards() {
    document.removeEventListener("visibilitychange", this._onVisibility)
    window.removeEventListener("pagehide", this._onPageHide)
    document.removeEventListener("ranging-barcode:close", this._onExternalClose)
  }

  _setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }
}
